import React, { useState } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { auth } from '../firebase';
import { useTheme } from '../ThemeContext';
import useBreakpoint from '../hooks/useBreakpoint';

export default function AuthScreen({ onAuthSuccess, initialMode = 'signin', onCancel }) {
  const { t } = useTheme();
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const isTablet = bp === 'tablet';
  const isDesktop = bp === 'desktop';

  const [mode, setMode] = useState(initialMode); // 'signin' | 'register' | 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);

  const clearMessages = () => {
    setError('');
    setResetSent(false);
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    clearMessages();
  };

  const friendlyError = (err) => {
    const code = err?.code || '';
    if (code === 'auth/user-not-found' || code === 'auth/wrong-password' || code === 'auth/invalid-credential')
      return 'Incorrect email or password.';
    if (code === 'auth/email-already-in-use') return 'An account with this email already exists.';
    if (code === 'auth/weak-password') return 'Password must be at least 6 characters.';
    if (code === 'auth/invalid-email') return 'Please enter a valid email address.';
    if (code === 'auth/too-many-requests') return 'Too many attempts. Please try again later.';
    if (code === 'auth/popup-closed-by-user') return 'Sign-in popup was closed. Please try again.';
    return err?.message || 'Something went wrong. Please try again.';
  };

  const handleGoogleSignIn = async () => {
    clearMessages();
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      onAuthSuccess(result.user);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);

    try {
      if (mode === 'reset') {
        await sendPasswordResetEmail(auth, email);
        setResetSent(true);
        setLoading(false);
        return;
      }

      let result;
      if (mode === 'signin') {
        result = await signInWithEmailAndPassword(auth, email, password);
      } else {
        result = await createUserWithEmailAndPassword(auth, email, password);
      }
      onAuthSuccess(result.user);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitLabel = () => {
    if (loading) return 'Please wait…';
    if (mode === 'signin') return 'Sign In';
    if (mode === 'register') return 'Create Account';
    return 'Send Reset Email';
  };

  // Responsive card padding
  const cardPadding = isDesktop ? '40px 36px' : isTablet ? '32px 28px' : '28px 20px';

  return (
    <div style={{
      minHeight: '100vh',
      background: t.pageBg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      <div style={{
        background: t.card,
        borderRadius: '12px',
        boxShadow: t.shadowMed,
        padding: cardPadding,
        width: '100%',
        maxWidth: '400px',
      }}>
        {/* Branding */}
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{ fontSize: '22px', fontWeight: '800', letterSpacing: '-0.02em', marginBottom: '8px', color: '#1a1c1d' }}>BPP</div>
          <h1 style={{
            margin: '0 0 4px',
            fontSize: '22px',
            fontWeight: '800',
            color: t.text,
            letterSpacing: '-0.02em',
          }}>
            Budget Planner Pro
          </h1>
          {mode === 'register' ? (
            <p style={{ margin: 0, fontSize: '13px', color: t.textMuted, lineHeight: '1.6' }}>
              Cloud sync · Multiple trackers · Savings goals<br />
              Extended projections · Full debt amortization<br />
              <strong style={{ color: t.text }}>$6 / month</strong> — cancel any time
            </p>
          ) : (
            <p style={{ margin: 0, fontSize: '13px', color: t.textMuted }}>
              Your finances, secured.
            </p>
          )}
        </div>

        {/* Mode Tabs (hidden in reset mode) */}
        {mode !== 'reset' && (
          <div style={{
            display: 'flex',
            background: t.pageBg,
            borderRadius: '8px',
            padding: '3px',
            marginBottom: '24px',
          }}>
            {[
              { key: 'signin', label: 'Sign In' },
              { key: 'register', label: 'Create Account' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleModeChange(tab.key)}
                style={{
                  flex: 1,
                  padding: '8px',
                  border: 'none',
                  borderRadius: '6px',
                  background: mode === tab.key ? t.card : 'transparent',
                  color: mode === tab.key ? t.text : t.textMuted,
                  fontWeight: mode === tab.key ? '600' : '400',
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  boxShadow: mode === tab.key ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
                  transition: 'background 0.15s, color 0.15s',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Reset mode back link */}
        {mode === 'reset' && (
          <div style={{ marginBottom: '20px' }}>
            <button
              onClick={() => handleModeChange('signin')}
              style={{
                background: 'none',
                border: 'none',
                color: '#0061a4',
                fontSize: '13px',
                cursor: 'pointer',
                padding: 0,
                fontFamily: 'inherit',
              }}
            >
              ← Back to Sign In
            </button>
            <h2 style={{ margin: '8px 0 4px', fontSize: '18px', fontWeight: '700', color: t.text }}>
              Reset Password
            </h2>
            <p style={{ margin: 0, fontSize: '13px', color: t.textMuted }}>
              Enter your email and we'll send you a reset link.
            </p>
          </div>
        )}

        {/* Error Banner */}
        {error && (
          <div style={{
            background: '#fce4ec',
            color: '#ba1a1a',
            borderRadius: '8px',
            padding: '10px 14px',
            fontSize: '13px',
            marginBottom: '16px',
            lineHeight: '1.4',
          }}>
            {error}
          </div>
        )}

        {/* Success Banner */}
        {resetSent && (
          <div style={{
            background: '#e8f5e9',
            color: '#1b6d24',
            borderRadius: '8px',
            padding: '10px 14px',
            fontSize: '13px',
            marginBottom: '16px',
            lineHeight: '1.4',
          }}>
            Password reset email sent! Check your inbox.
          </div>
        )}

        {/* Google Sign-In (not in reset mode) */}
        {mode !== 'reset' && (
          <>
            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              style={{
                width: '100%',
                padding: '11px',
                background: t.card,
                border: '1px solid rgba(191,199,212,0.4)',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: t.textSub,
                cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                marginBottom: '16px',
                transition: 'border-color 0.15s, box-shadow 0.15s',
              }}
            >
              <GoogleIcon />
              Continue with Google
            </button>

            {/* OR Divider */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px',
            }}>
              <div style={{ flex: 1, height: '1px', background: t.border }} />
              <span style={{ fontSize: '12px', color: t.textFaint, fontWeight: '500' }}>OR</span>
              <div style={{ flex: 1, height: '1px', background: t.border }} />
            </div>
          </>
        )}

        {/* Guest back link */}
        {onCancel && mode !== 'reset' && (
          <div style={{ textAlign: 'center', marginBottom: '14px' }}>
            <button
              type="button"
              onClick={onCancel}
              style={{
                background: 'none', border: 'none', color: t.textFaintest,
                fontSize: '12px', cursor: 'pointer', padding: 0, fontFamily: 'inherit',
              }}
            >
              ← Continue in guest mode (data may be lost)
            </button>
          </div>
        )}

        {/* Email/Password Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: t.textMid, marginBottom: '6px' }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid rgba(191,199,212,0.4)',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'inherit',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.15s',
                background: t.inputBg,
                color: t.text,
              }}
              onFocus={(e) => (e.target.style.borderColor = '#0061a4')}
              onBlur={(e) => (e.target.style.borderColor = 'rgba(191,199,212,0.4)')}
            />
          </div>

          {mode !== 'reset' && (
            <div style={{ marginBottom: '6px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: t.textMid, marginBottom: '6px' }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid rgba(191,199,212,0.4)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.15s',
                  background: t.inputBg,
                  color: t.text,
                }}
                onFocus={(e) => (e.target.style.borderColor = '#0061a4')}
                onBlur={(e) => (e.target.style.borderColor = '#ddd')}
              />
            </div>
          )}

          {mode === 'signin' && (
            <div style={{ textAlign: 'right', marginBottom: '20px' }}>
              <button
                type="button"
                onClick={() => handleModeChange('reset')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#0061a4',
                  fontSize: '12px',
                  cursor: 'pointer',
                  padding: '2px 0',
                  fontFamily: 'inherit',
                }}
              >
                Forgot Password?
              </button>
            </div>
          )}

          {mode !== 'signin' && <div style={{ marginBottom: '20px' }} />}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              background: loading ? '#90caf9' : '#0061a4',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'background 0.15s',
            }}
          >
            {loading && <Spinner />}
            {submitLabel()}
          </button>
        </form>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}

function Spinner() {
  return (
    <span style={{
      display: 'inline-block',
      width: '14px',
      height: '14px',
      border: '2px solid rgba(255,255,255,0.4)',
      borderTopColor: 'white',
      borderRadius: '50%',
      animation: 'spin 0.7s linear infinite',
    }} />
  );
}
